import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  listManagers,
  type ApiManagerStatus,
  type ApiTaskCheckpoint,
} from "@/lib/api";
import {
  ManagerConnectionContext,
  type AgentState,
  type InstanceSessionState,
  type LivestreamConnectionState,
  type ManagerConnectionContextValue,
  type SendMessageInput,
  type SendMessageResult,
  type ShutdownInstanceInput,
  type ShutdownInstanceResult,
  type SocketState,
  type WakeInstanceInput,
  type WakeInstanceResult,
} from "@/components/ManagerConnectionContext";
import {
  isActiveManagerConnected,
  markInstanceSessionLivestreamOffline,
  shouldIgnoreOfflineReplayRestart,
} from "@/components/manager-connection-utils";
import { supabase } from "@/lib/supabase";

type ManagerEvent =
  | { type: "connection_ready" }
  | { type: "authenticated" }
  | {
      type: "manager_auth_state";
      reauth_required?: boolean;
      reauth_session_id?: string | null;
      reauth_url?: string | null;
    }
  | {
      type: "terminal_replay_start";
      instance_id?: string;
      replay_id?: string;
      end_sequence?: number;
      cols?: number;
      rows?: number;
    }
  | {
      type: "terminal_replay_chunk";
      instance_id?: string;
      replay_id?: string;
      sequence?: number;
      data_base64?: string;
    }
  | {
      type: "terminal_replay_resize";
      instance_id?: string;
      replay_id?: string;
      cols?: number;
      rows?: number;
    }
  | {
      type: "terminal_replay_end";
      instance_id?: string;
      replay_id?: string;
      end_sequence?: number;
      cols?: number;
      rows?: number;
    }
  | {
      type: "terminal_output";
      instance_id?: string;
      sequence?: number;
      data_base64?: string;
    }
  | {
      type: "livestream_state";
      instance_id?: string;
      state?: LivestreamConnectionState;
      message?: string | null;
    }
  | {
      type: "agent_state";
      instance_id?: string;
      state?: AgentState;
      task_id?: string | null;
      task_name?: string | null;
      working_dir?: string | null;
      message?: string | null;
    }
  | {
      type: "task_checkpoints";
      instance_id?: string;
      task_id?: string | null;
      checkpoints?: ApiTaskCheckpoint[];
    }
  | {
      type: "message_accepted";
      client_message_id?: string;
      command?: { id?: string | null } | null;
    }
  | {
      type: "folder_action_complete";
      client_message_id?: string;
    }
  | {
      type: "wake_accepted";
      client_message_id?: string;
      status?: "accepted" | "capacity_blocked";
      code?: string;
      running_count?: number;
      max_concurrency?: number;
    }
  | {
      type: "shutdown_accepted";
      client_message_id?: string;
    }
  | {
      type: "error";
      client_message_id?: string;
      message?: string;
    };

type SyncableSupabaseSession = {
  access_token?: string | null;
  refresh_token?: string | null;
  expires_at?: number | null;
  user?: {
    id?: string | null;
  } | null;
};

function buildUserSessionSyncPayload(session: SyncableSupabaseSession | null) {
  const accessToken = session?.access_token?.trim();
  const refreshToken = session?.refresh_token?.trim();
  const userId = session?.user?.id?.trim();
  const expiresAt = Number(session?.expires_at ?? 0);
  if (
    !accessToken ||
    !refreshToken ||
    !userId ||
    !Number.isFinite(expiresAt) ||
    expiresAt <= 0
  ) {
    return null;
  }
  return {
    type: "sync_user_session",
    access_token: accessToken,
    refresh_token: refreshToken,
    expires_at: expiresAt,
    user_id: userId,
  };
}

const MAX_TERMINAL_CHUNKS = 512;
const RECONNECT_DELAYS_MS = [250, 750, 1500, 3000, 5000, 8000];
const NORMAL_SOCKET_CLOSE_CODE = 1000;
const NORMAL_SOCKET_CLOSE_REASON = "client reconnect";
const PENDING_MESSAGE_TIMEOUT_MS = 15000;
const PENDING_FOLDER_ACTION_TIMEOUT_MS = 30000;
const MANAGER_POLL_FAILURE_THRESHOLD = 3;

function emptyInstanceSessionState(
  current?: Partial<InstanceSessionState> | null,
): InstanceSessionState {
  return {
    terminalChunks: current?.terminalChunks ?? [],
    terminalReplay: current?.terminalReplay ?? null,
    terminalReplayRequestId: current?.terminalReplayRequestId ?? 0,
    terminalReplayInProgress: current?.terminalReplayInProgress ?? false,
    terminalReplayEndSequence: current?.terminalReplayEndSequence ?? 0,
    lastTerminalSequence: current?.lastTerminalSequence ?? 0,
    managerSubdomain: current?.managerSubdomain ?? null,
    livestreamState: current?.livestreamState ?? null,
    agentState: current?.agentState ?? null,
    agentStateMessage: current?.agentStateMessage ?? null,
    livestreamMessage: current?.livestreamMessage ?? null,
    workingDirectory: current?.workingDirectory ?? null,
    taskId: current?.taskId ?? null,
    taskName: current?.taskName ?? null,
    taskCheckpoints: current?.taskCheckpoints ?? [],
  };
}

export function applyAgentStateToSession(
  currentSession: Partial<InstanceSessionState> | null | undefined,
  payload: {
    state?: AgentState | null;
    task_id?: string | null;
    task_name?: string | null;
    working_dir?: string | null;
    message?: string | null;
  },
  subdomain: string | null,
): InstanceSessionState {
  const current = emptyInstanceSessionState(currentSession);
  const newAgentState = payload.state ?? null;
  const payloadTaskId = payload.task_id?.trim() || null;
  const payloadTaskName = payload.task_name?.trim() || null;
  const payloadWorkingDirectory = payload.working_dir?.trim() || null;
  const taskChanged =
    payloadTaskId !== null && payloadTaskId !== current.taskId;
  const shouldClearTaskState =
    payloadTaskId === null &&
    (newAgentState === "idle" ||
      newAgentState === "offline" ||
      newAgentState === "waking_up");
  const shouldClearTerminal = newAgentState === "waking_up";
  const preservedOnlineSequence =
    shouldClearTerminal && current.livestreamState === "online"
      ? current.lastTerminalSequence
      : 0;

  return {
    ...current,
    managerSubdomain: subdomain,
    agentState: newAgentState,
    agentStateMessage: payload.message ?? null,
    taskId: shouldClearTaskState ? null : (payloadTaskId ?? current.taskId),
    taskName: shouldClearTaskState
      ? null
      : (payloadTaskName ?? current.taskName),
    workingDirectory: shouldClearTaskState
      ? null
      : (payloadWorkingDirectory ?? current.workingDirectory),
    taskCheckpoints:
      shouldClearTaskState || taskChanged ? [] : current.taskCheckpoints,
    ...(shouldClearTerminal
      ? {
          terminalReplay: null,
          terminalChunks: [],
          terminalReplayRequestId: current.terminalReplayRequestId + 1,
          terminalReplayInProgress: false,
          terminalReplayEndSequence: preservedOnlineSequence,
          lastTerminalSequence: preservedOnlineSequence,
        }
      : {}),
  };
}

function chooseActiveManager(
  managers: ApiManagerStatus[],
  preferredSubdomain?: string | null,
): ApiManagerStatus | null {
  const managersWithSubdomain = managers.filter(
    (manager) =>
      typeof manager.subdomain === "string" &&
      manager.subdomain.trim().length > 0,
  );
  const onlineManagers = managers.filter(
    (manager) =>
      manager.online === true &&
      typeof manager.subdomain === "string" &&
      manager.subdomain.trim().length > 0,
  );
  const reauthManagers = onlineManagers.filter(
    (manager) => manager.reauthRequired === true,
  );
  const trimmedPreferred = preferredSubdomain?.trim();
  if (trimmedPreferred) {
    const preferredReauth = reauthManagers.find(
      (manager) => manager.subdomain?.trim() === trimmedPreferred,
    );
    if (preferredReauth) {
      return preferredReauth;
    }
    const preferred = onlineManagers.find(
      (manager) => manager.subdomain?.trim() === trimmedPreferred,
    );
    if (preferred) {
      return preferred;
    }
  }
  if (reauthManagers[0]) {
    return reauthManagers[0];
  }
  return onlineManagers[0] ?? managersWithSubdomain[0] ?? null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function mergeManagerReauthState(
  manager: ApiManagerStatus,
  override?: { reauthRequired: boolean; reauthSessionId: string | null } | null,
): ApiManagerStatus {
  if (!override) {
    return manager;
  }

  if (override.reauthRequired) {
    const overrideSessionId =
      typeof override.reauthSessionId === "string" &&
      override.reauthSessionId.trim()
        ? override.reauthSessionId
        : null;
    const managerSessionId =
      typeof manager.reauthSessionId === "string" &&
      manager.reauthSessionId.trim()
        ? manager.reauthSessionId
        : null;
    return {
      ...manager,
      reauthRequired: true,
      reauthSessionId: overrideSessionId ?? managerSessionId,
    };
  }

  if (manager.reauthRequired !== false || manager.reauthSessionId !== null) {
    return {
      ...manager,
      reauthRequired: false,
      reauthSessionId: null,
    };
  }

  const nextSessionId =
    typeof manager.reauthSessionId === "string"
      ? manager.reauthSessionId
      : override.reauthSessionId;

  if (
    manager.reauthRequired === override.reauthRequired &&
    manager.reauthSessionId === nextSessionId
  ) {
    return manager;
  }

  return {
    ...manager,
    reauthRequired: override.reauthRequired,
    reauthSessionId: nextSessionId,
  };
}

function decodeBase64ToBytes(dataBase64: string): Uint8Array {
  const binary = window.atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function closeManagerSocket(
  socket: WebSocket | null,
  reason = NORMAL_SOCKET_CLOSE_REASON,
) {
  if (!socket) {
    return;
  }
  if (
    socket.readyState !== WebSocket.OPEN &&
    socket.readyState !== WebSocket.CONNECTING
  ) {
    return;
  }
  socket.close(NORMAL_SOCKET_CLOSE_CODE, reason);
}

export function ManagerConnectionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [managerSubdomain, setManagerSubdomain] = useState<string | null>(null);
  const [activeManagerId, setActiveManagerId] = useState<string | null>(null);
  const [activeManagerStatus, setActiveManagerStatus] =
    useState<ApiManagerStatus | null>(null);
  const [preferredManagerSubdomain, setPreferredManagerSubdomain] = useState<
    string | null
  >(null);
  const [socketState, setSocketState] = useState<SocketState>("idle");
  const [onlineManagerSubdomains, setOnlineManagerSubdomains] = useState<
    string[]
  >([]);
  const [socketStatesBySubdomain, setSocketStatesBySubdomain] = useState<
    Record<string, SocketState>
  >({});
  const [instanceSessions, setInstanceSessions] = useState<
    Record<string, InstanceSessionState>
  >({});
  const websocketsRef = useRef<Map<string, WebSocket>>(new Map());
  const socketGenerationsRef = useRef<Map<string, number>>(new Map());
  const reconnectTimersRef = useRef<Map<string, number>>(new Map());
  const reconnectAttemptsRef = useRef<Map<string, number>>(new Map());
  const authenticatedRefs = useRef<Map<string, boolean>>(new Map());
  const managerSubdomainRef = useRef<string | null>(null);
  const activeManagerStatusRef = useRef<ApiManagerStatus | null>(null);
  const onlineManagerSubdomainsRef = useRef<string[]>([]);
  const socketStatesRef = useRef<Map<string, SocketState>>(new Map());
  const instanceSessionsRef = useRef<Record<string, InstanceSessionState>>({});
  const desiredInstanceSubscriptionsRef = useRef<Set<string>>(new Set());
  const activeInstanceSubscriptionsRef = useRef<Set<string>>(new Set());
  const desiredInstanceTerminalSizesRef = useRef<
    Map<string, { cols: number; rows: number }>
  >(new Map());
  const consecutiveManagerPollFailuresRef = useRef(0);
  const managerReauthStateRef = useRef<
    Map<string, { reauthRequired: boolean; reauthSessionId: string | null }>
  >(new Map());
  const nextChunkIdRef = useRef(0);
  const nextReplayIdRef = useRef(0);
  const replayAssembliesRef = useRef<
    Map<
      string,
      {
        replayId: string;
        endSequence: number;
        cols: number;
        rows: number;
        steps: Array<
          | { kind: "output"; data: Uint8Array }
          | { kind: "resize"; cols: number; rows: number }
        >;
      }
    >
  >(new Map());
  const pendingMessagesRef = useRef<
    Map<
      string,
      {
        resolve: (value: SendMessageResult) => void;
        reject: (reason?: unknown) => void;
        timeoutId: number;
      }
    >
  >(new Map());
  const pendingFolderActionsRef = useRef<
    Map<
      string,
      {
        resolve: () => void;
        reject: (reason?: unknown) => void;
        timeoutId: number;
      }
    >
  >(new Map());
  const pendingWakeRequestsRef = useRef<
    Map<
      string,
      {
        targetSubdomain: string | null;
        payload: {
          type: "wake_instance";
          client_message_id: string;
          instance_id: string;
          task_id?: string;
        };
        sent: boolean;
        resolve: (value: WakeInstanceResult) => void;
        reject: (reason?: unknown) => void;
        timeoutId: number;
      }
    >
  >(new Map());
  const pendingShutdownRequestsRef = useRef<
    Map<
      string,
      {
        targetSubdomain: string | null;
        payload: {
          type: "shutdown_instance";
          client_message_id: string;
          instance_id: string;
        };
        sent: boolean;
        resolve: (value: ShutdownInstanceResult) => void;
        reject: (reason?: unknown) => void;
        timeoutId: number;
      }
    >
  >(new Map());

  const rejectPendingMessages = (message: string) => {
    for (const [key, pending] of pendingMessagesRef.current.entries()) {
      window.clearTimeout(pending.timeoutId);
      pending.reject(new Error(message));
      pendingMessagesRef.current.delete(key);
    }
    for (const [key, pending] of pendingFolderActionsRef.current.entries()) {
      window.clearTimeout(pending.timeoutId);
      pending.reject(new Error(message));
      pendingFolderActionsRef.current.delete(key);
    }
  };

  const rejectPendingWakeRequests = (message: string) => {
    for (const [key, pending] of pendingWakeRequestsRef.current.entries()) {
      window.clearTimeout(pending.timeoutId);
      pending.reject(new Error(message));
      pendingWakeRequestsRef.current.delete(key);
    }
    for (const [key, pending] of pendingShutdownRequestsRef.current.entries()) {
      window.clearTimeout(pending.timeoutId);
      pending.reject(new Error(message));
      pendingShutdownRequestsRef.current.delete(key);
    }
  };

  const markPendingWakeRequestsForRetry = () => {
    for (const pending of pendingWakeRequestsRef.current.values()) {
      pending.sent = false;
    }
    for (const pending of pendingShutdownRequestsRef.current.values()) {
      pending.sent = false;
    }
  };

  const sendBrowserSessionToManager = useCallback(
    async (subdomain: string, socket: WebSocket) => {
      const { data } = await supabase.auth.getSession();
      const payload = buildUserSessionSyncPayload(
        data.session as SyncableSupabaseSession | null,
      );
      if (
        !payload ||
        websocketsRef.current.get(subdomain) !== socket ||
        socket.readyState !== WebSocket.OPEN ||
        !authenticatedRefs.current.get(subdomain)
      ) {
        return;
      }
      socket.send(JSON.stringify(payload));
    },
    [],
  );

  useEffect(() => {
    managerSubdomainRef.current = managerSubdomain;
  }, [managerSubdomain]);

  useEffect(() => {
    activeManagerStatusRef.current = activeManagerStatus;
  }, [activeManagerStatus]);

  useEffect(() => {
    onlineManagerSubdomainsRef.current = onlineManagerSubdomains;
  }, [onlineManagerSubdomains]);

  useEffect(() => {
    instanceSessionsRef.current = instanceSessions;
  }, [instanceSessions]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== "SIGNED_IN" && event !== "TOKEN_REFRESHED") {
        return;
      }
      const payload = buildUserSessionSyncPayload(
        session as SyncableSupabaseSession | null,
      );
      if (!payload) {
        return;
      }
      for (const [subdomain, socket] of websocketsRef.current.entries()) {
        if (
          socket.readyState === WebSocket.OPEN &&
          authenticatedRefs.current.get(subdomain)
        ) {
          socket.send(JSON.stringify(payload));
        }
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadManagers = async () => {
      try {
        const managers = (await listManagers()).map((manager) => {
          return mergeManagerReauthState(
            manager,
            managerReauthStateRef.current.get(manager.id) ?? null,
          );
        });
        if (!active) {
          return;
        }
        consecutiveManagerPollFailuresRef.current = 0;

        const online = new Set<string>();
        for (const m of managers) {
          if (m.online && m.subdomain) {
            online.add(m.subdomain.trim());
          }
        }

        const activeManager = chooseActiveManager(
          managers,
          preferredManagerSubdomain,
        );
        const activeSubdomain = activeManager?.subdomain?.trim() ?? null;
        const connectableManagers = new Set(online);
        if (activeSubdomain) {
          connectableManagers.add(activeSubdomain);
        }
        const nextOnlineManagerSubdomains =
          Array.from(connectableManagers).sort();

        setActiveManagerId(activeManager?.id ?? null);
        setActiveManagerStatus(activeManager ?? null);
        setManagerSubdomain(activeSubdomain);
        setOnlineManagerSubdomains((prev) => {
          if (
            prev.length === nextOnlineManagerSubdomains.length &&
            prev.every(
              (value, index) => value === nextOnlineManagerSubdomains[index],
            )
          ) {
            return prev;
          }
          return nextOnlineManagerSubdomains;
        });
        setSocketStatesBySubdomain((prev) => {
          const next = { ...prev };
          let changed = false;
          // Clean up offline managers from state (only if we don't have an active websocket for them)
          for (const sub of Object.keys(next)) {
            if (
              !connectableManagers.has(sub) &&
              !websocketsRef.current.has(sub)
            ) {
              delete next[sub];
              changed = true;
            }
          }
          // Initialize connectable managers in state if missing
          for (const sub of connectableManagers) {
            if (!(sub in next)) {
              next[sub] = "idle";
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      } catch {
        if (!active) {
          return;
        }
        consecutiveManagerPollFailuresRef.current += 1;
        // Do not clear the manager or connection states on poll failures to avoid transient flashing/disconnections.
      }
    };

    void loadManagers();
    const timer = window.setInterval(loadManagers, 10000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [preferredManagerSubdomain]);

  const updateSocketState = useCallback(
    (subdomain: string, state: SocketState) => {
      socketStatesRef.current.set(subdomain, state);
      setSocketStatesBySubdomain((prev) => ({
        ...prev,
        [subdomain]: state,
      }));
      if (subdomain === managerSubdomainRef.current) {
        setSocketState(state);
      }
    },
    [],
  );

  const markInstanceSessionsOffline = useCallback((subdomain: string) => {
    const trimmedSubdomain = subdomain.trim();
    if (!trimmedSubdomain) {
      return;
    }
    setInstanceSessions((prev) => {
      let changed = false;
      const next: Record<string, InstanceSessionState> = {};
      for (const [instanceId, session] of Object.entries(prev)) {
        if (session.managerSubdomain !== trimmedSubdomain) {
          next[instanceId] = session;
          continue;
        }
        changed = true;
        next[instanceId] = markInstanceSessionLivestreamOffline(session);
      }
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    const current = managerSubdomain
      ? socketStatesBySubdomain[managerSubdomain]
      : "idle";
    setSocketState(current || "offline");
  }, [managerSubdomain, socketStatesBySubdomain]);

  const managerOnline = useMemo(
    () => isActiveManagerConnected(managerSubdomain, socketStatesBySubdomain),
    [managerSubdomain, socketStatesBySubdomain],
  );

  const sendRaw = useCallback(
    (payload: Record<string, unknown>, subdomain?: string | null) => {
      const target = subdomain || managerSubdomainRef.current;
      if (!target) {
        throw new Error("No target manager subdomain specified.");
      }
      const ws = websocketsRef.current.get(target);
      if (
        !ws ||
        ws.readyState !== WebSocket.OPEN ||
        !authenticatedRefs.current.get(target)
      ) {
        throw new Error(`Manager websocket (${target}) is not connected.`);
      }
      ws.send(JSON.stringify(payload));
    },
    [],
  );

  const resolveTargetSubdomain = useCallback(
    (instanceId?: string | null, explicitSubdomain?: string | null) => {
      const direct = explicitSubdomain?.trim();
      if (direct) {
        return direct;
      }
      const trimmedInstanceId = instanceId?.trim();
      if (trimmedInstanceId) {
        const session = instanceSessionsRef.current[trimmedInstanceId];
        const instanceSubdomain = session?.managerSubdomain?.trim();
        if (instanceSubdomain) {
          return instanceSubdomain;
        }
      }
      return managerSubdomainRef.current;
    },
    [],
  );

  const sendSubscribeInstance = useCallback(
    (instanceId: string, subdomain?: string | null) => {
      const trimmed = instanceId.trim();
      if (!trimmed) {
        return false;
      }
      const target = resolveTargetSubdomain(trimmed, subdomain);

      if (!target) return false;

      const size = desiredInstanceTerminalSizesRef.current.get(trimmed);
      if (!size || size.cols <= 0 || size.rows <= 0) {
        return false;
      }
      try {
        sendRaw(
          {
            type: "subscribe_instance",
            instance_id: trimmed,
            cols: size.cols,
            rows: size.rows,
          },
          target,
        );
        activeInstanceSubscriptionsRef.current.add(trimmed);
        return true;
      } catch {
        return false;
      }
    },
    [resolveTargetSubdomain, sendRaw],
  );

  useEffect(() => {
    const subdomains = onlineManagerSubdomains;
    let cancelled = false;

    const scheduleReconnect = (subdomain: string) => {
      if (cancelled || reconnectTimersRef.current.has(subdomain)) {
        return;
      }
      updateSocketState(subdomain, "reconnecting");
      const currentAttempts = reconnectAttemptsRef.current.get(subdomain) || 0;
      const attemptIndex = Math.min(
        RECONNECT_DELAYS_MS.length - 1,
        currentAttempts,
      );
      const delay = RECONNECT_DELAYS_MS[attemptIndex] ?? 8000;

      reconnectAttemptsRef.current.set(subdomain, currentAttempts + 1);

      const timer = window.setTimeout(() => {
        reconnectTimersRef.current.delete(subdomain);
        if (!cancelled) {
          connect(subdomain);
        }
      }, delay);
      reconnectTimersRef.current.set(subdomain, timer);
    };

    const connect = (subdomain: string) => {
      if (cancelled) return;

      authenticatedRefs.current.set(subdomain, false);
      updateSocketState(
        subdomain,
        (reconnectAttemptsRef.current.get(subdomain) || 0) > 0
          ? "reconnecting"
          : "connecting",
      );

      const previous = websocketsRef.current.get(subdomain);
      if (previous) {
        previous.onopen = null;
        previous.onmessage = null;
        previous.onerror = null;
        previous.onclose = null;
        closeManagerSocket(previous);
      }

      const ws = new WebSocket(
        `wss://${subdomain}.passiveagents.com/ws/manager`,
      );
      const generation = (socketGenerationsRef.current.get(subdomain) || 0) + 1;
      socketGenerationsRef.current.set(subdomain, generation);
      websocketsRef.current.set(subdomain, ws);

      ws.onopen = () => {
        if (
          cancelled ||
          websocketsRef.current.get(subdomain) !== ws ||
          socketGenerationsRef.current.get(subdomain) !== generation
        ) {
          return;
        }

        const authenticate = async () => {
          const { data } = await supabase.auth.getSession();
          if (cancelled || websocketsRef.current.get(subdomain) !== ws) return;

          const accessToken = data.session?.access_token?.trim();
          if (!accessToken) {
            updateSocketState(subdomain, "error");
            closeManagerSocket(ws, "missing access token");
            return;
          }
          ws.send(
            JSON.stringify({ type: "authenticate", access_token: accessToken }),
          );
        };
        void authenticate();
      };

      ws.onmessage = (event) => {
        if (cancelled || websocketsRef.current.get(subdomain) !== ws) return;
        let payload: ManagerEvent;
        try {
          payload = JSON.parse(String(event.data)) as ManagerEvent;
        } catch {
          return;
        }

        if (payload.type === "connection_ready") {
          updateSocketState(subdomain, "connecting");
          return;
        }

        if (payload.type === "authenticated") {
          authenticatedRefs.current.set(subdomain, true);
          reconnectAttemptsRef.current.set(subdomain, 0);
          updateSocketState(subdomain, "connected");
          void sendBrowserSessionToManager(subdomain, ws);
          for (const instanceId of desiredInstanceSubscriptionsRef.current) {
            try {
              sendSubscribeInstance(instanceId);
            } catch {
              // If the socket drops immediately after auth, reconnect handling will retry.
            }
          }
          for (const pending of pendingWakeRequestsRef.current.values()) {
            if (
              pending.sent ||
              (pending.targetSubdomain && pending.targetSubdomain !== subdomain)
            ) {
              continue;
            }
            ws.send(JSON.stringify(pending.payload));
            pending.sent = true;
          }
          for (const pending of pendingShutdownRequestsRef.current.values()) {
            if (
              pending.sent ||
              (pending.targetSubdomain && pending.targetSubdomain !== subdomain)
            ) {
              continue;
            }
            ws.send(JSON.stringify(pending.payload));
            pending.sent = true;
          }
          return;
        }

        if (payload.type === "manager_auth_state") {
          const currentActiveStatus = activeManagerStatusRef.current;
          const matchesActiveManager =
            currentActiveStatus?.subdomain?.trim() === subdomain;
          const managerId = matchesActiveManager
            ? currentActiveStatus?.id
            : null;
          const reauthRequired = payload.reauth_required === true;
          const reauthSessionId =
            typeof payload.reauth_session_id === "string"
              ? payload.reauth_session_id.trim() || null
              : null;
          if (managerId) {
            managerReauthStateRef.current.set(managerId, {
              reauthRequired,
              reauthSessionId,
            });
          }
          setActiveManagerStatus((previous) => {
            if (!previous || previous.subdomain?.trim() !== subdomain) {
              return previous;
            }
            return {
              ...previous,
              reauthRequired,
              reauthSessionId,
            };
          });
          return;
        }

        if (payload.type === "terminal_replay_start") {
          const instanceId = payload.instance_id?.trim();
          const replayId = payload.replay_id?.trim();
          const endSequence = Math.max(0, Number(payload.end_sequence ?? 0));
          const cols = Math.max(0, Number(payload.cols ?? 0));
          const rows = Math.max(0, Number(payload.rows ?? 0));
          if (!instanceId || !replayId) return;

          replayAssembliesRef.current.set(instanceId, {
            replayId,
            endSequence,
            cols,
            rows,
            steps: [],
          });

          setInstanceSessions((prev) => {
            const current = emptyInstanceSessionState(prev[instanceId]);
            if (endSequence > 0 && endSequence < current.lastTerminalSequence) {
              replayAssembliesRef.current.delete(instanceId);
              return prev;
            }
            if (shouldIgnoreOfflineReplayRestart(current, endSequence)) {
              replayAssembliesRef.current.delete(instanceId);
              return prev;
            }
            return {
              ...prev,
              [instanceId]: {
                ...current,
                terminalChunks: [],
                terminalReplay: null,
                terminalReplayRequestId: current.terminalReplayRequestId + 1,
                terminalReplayInProgress: true,
                terminalReplayEndSequence: endSequence,
                lastTerminalSequence: Math.max(
                  current.lastTerminalSequence,
                  endSequence,
                ),
                managerSubdomain: subdomain,
              },
            };
          });
          return;
        }

        if (payload.type === "terminal_replay_chunk") {
          const instanceId = payload.instance_id?.trim();
          const replayId = payload.replay_id?.trim();
          const dataBase64 = payload.data_base64?.trim() ?? "";
          if (!instanceId || !replayId || dataBase64.length === 0) {
            return;
          }
          const assembly = replayAssembliesRef.current.get(instanceId);
          if (!assembly || assembly.replayId !== replayId) {
            return;
          }
          assembly.steps.push({
            kind: "output",
            data: decodeBase64ToBytes(dataBase64),
          });
          return;
        }

        if (payload.type === "terminal_replay_resize") {
          const instanceId = payload.instance_id?.trim();
          const replayId = payload.replay_id?.trim();
          const cols = Math.max(0, Number(payload.cols ?? 0));
          const rows = Math.max(0, Number(payload.rows ?? 0));
          if (!instanceId || !replayId || cols <= 0 || rows <= 0) {
            return;
          }
          const assembly = replayAssembliesRef.current.get(instanceId);
          if (!assembly || assembly.replayId !== replayId) {
            return;
          }
          assembly.steps.push({ kind: "resize", cols, rows });
          return;
        }

        if (payload.type === "terminal_replay_end") {
          const instanceId = payload.instance_id?.trim();
          const replayId = payload.replay_id?.trim();
          if (!instanceId || !replayId) {
            return;
          }
          const assembly = replayAssembliesRef.current.get(instanceId);
          if (!assembly || assembly.replayId !== replayId) {
            return;
          }
          replayAssembliesRef.current.delete(instanceId);
          const endSequence = Math.max(
            assembly.endSequence,
            Number(payload.end_sequence ?? 0),
          );
          const cols = Math.max(0, Number(payload.cols ?? assembly.cols ?? 0));
          const rows = Math.max(0, Number(payload.rows ?? assembly.rows ?? 0));
          const replayIdNumber = nextReplayIdRef.current + 1;
          nextReplayIdRef.current = replayIdNumber;
          setInstanceSessions((prev) => {
            const current = emptyInstanceSessionState(prev[instanceId]);
            const filteredChunks = current.terminalChunks.filter((chunk) => {
              return chunk.sequence === 0 || chunk.sequence > endSequence;
            });
            return {
              ...prev,
              [instanceId]: {
                ...current,
                terminalChunks: filteredChunks.slice(-MAX_TERMINAL_CHUNKS),
                terminalReplay: {
                  id: replayIdNumber,
                  endSequence,
                  cols,
                  rows,
                  steps: assembly.steps,
                },
                terminalReplayInProgress: false,
                terminalReplayEndSequence: endSequence,
                lastTerminalSequence: Math.max(
                  current.lastTerminalSequence,
                  endSequence,
                ),
              },
            };
          });
          return;
        }

        if (payload.type === "terminal_output") {
          const instanceId = payload.instance_id?.trim();
          const dataBase64 = payload.data_base64?.trim() ?? "";
          const sequence = Math.max(0, Number(payload.sequence ?? 0));
          if (!instanceId || !dataBase64) {
            return;
          }
          const data = decodeBase64ToBytes(dataBase64);
          const chunkId = nextChunkIdRef.current + 1;
          nextChunkIdRef.current = chunkId;
          setInstanceSessions((prev) => {
            const current = emptyInstanceSessionState(prev[instanceId]);
            const replaySequence = current.terminalReplayInProgress
              ? current.terminalReplayEndSequence
              : (current.terminalReplay?.endSequence ??
                current.terminalReplayEndSequence);
            const lastChunkSequence =
              current.terminalChunks[current.terminalChunks.length - 1]
                ?.sequence ?? 0;
            if (sequence > 0 && sequence <= replaySequence) {
              return prev;
            }
            if (sequence > 0 && sequence <= lastChunkSequence) {
              return prev;
            }
            const nextChunks = [
              ...current.terminalChunks,
              { id: chunkId, sequence, data },
            ];
            return {
              ...prev,
              [instanceId]: {
                ...current,
                lastTerminalSequence: Math.max(
                  current.lastTerminalSequence,
                  sequence,
                ),
                terminalChunks: nextChunks.slice(-MAX_TERMINAL_CHUNKS),
              },
            };
          });
          return;
        }

        if (payload.type === "livestream_state") {
          const instanceId = payload.instance_id?.trim();
          if (!instanceId) {
            return;
          }
          setInstanceSessions((prev) => {
            const current = emptyInstanceSessionState(prev[instanceId]);
            return {
              ...prev,
              [instanceId]: {
                ...current,
                managerSubdomain: subdomain,
                livestreamState: payload.state ?? null,
                livestreamMessage: payload.message ?? null,
              },
            };
          });
          return;
        }

        if (payload.type === "agent_state") {
          const instanceId = payload.instance_id?.trim();
          if (!instanceId) {
            return;
          }
          setInstanceSessions((prev) => {
            return {
              ...prev,
              [instanceId]: applyAgentStateToSession(
                prev[instanceId],
                payload,
                subdomain,
              ),
            };
          });
          return;
        }

        if (payload.type === "task_checkpoints") {
          const instanceId = payload.instance_id?.trim();
          if (!instanceId) {
            return;
          }
          setInstanceSessions((prev) => {
            const current = emptyInstanceSessionState(prev[instanceId]);
            return {
              ...prev,
              [instanceId]: {
                ...current,
                managerSubdomain: subdomain,
                taskId: payload.task_id?.trim() || current.taskId,
                taskCheckpoints: Array.isArray(payload.checkpoints)
                  ? payload.checkpoints
                  : [],
              },
            };
          });
          return;
        }

        if (payload.type === "message_accepted") {
          const clientMessageId = payload.client_message_id?.trim();
          if (!clientMessageId) {
            return;
          }
          const pending = pendingMessagesRef.current.get(clientMessageId);
          if (!pending) {
            return;
          }
          pendingMessagesRef.current.delete(clientMessageId);
          window.clearTimeout(pending.timeoutId);
          pending.resolve({
            command: payload.command ?? null,
          });
          return;
        }

        if (payload.type === "folder_action_complete") {
          const clientMessageId = payload.client_message_id?.trim();
          if (!clientMessageId) {
            return;
          }
          const pending = pendingFolderActionsRef.current.get(clientMessageId);
          if (!pending) {
            return;
          }
          pendingFolderActionsRef.current.delete(clientMessageId);
          window.clearTimeout(pending.timeoutId);
          pending.resolve();
          return;
        }

        if (payload.type === "wake_accepted") {
          const clientMessageId = payload.client_message_id?.trim();
          if (!clientMessageId) {
            return;
          }
          const pending = pendingWakeRequestsRef.current.get(clientMessageId);
          if (
            !pending ||
            (pending.targetSubdomain && pending.targetSubdomain !== subdomain)
          ) {
            return;
          }
          pendingWakeRequestsRef.current.delete(clientMessageId);
          window.clearTimeout(pending.timeoutId);
          if (
            payload.status === "capacity_blocked" &&
            payload.code === "MAX_CONCURRENCY_REACHED"
          ) {
            pending.resolve({
              status: "capacity_blocked",
              code: "MAX_CONCURRENCY_REACHED",
              runningCount: payload.running_count ?? 0,
              maxConcurrency: payload.max_concurrency ?? 0,
            });
            return;
          }
          pending.resolve({ status: "accepted" });
          return;
        }

        if (payload.type === "shutdown_accepted") {
          const clientMessageId = payload.client_message_id?.trim();
          if (!clientMessageId) {
            return;
          }
          const pending =
            pendingShutdownRequestsRef.current.get(clientMessageId);
          if (
            !pending ||
            (pending.targetSubdomain && pending.targetSubdomain !== subdomain)
          ) {
            return;
          }
          pendingShutdownRequestsRef.current.delete(clientMessageId);
          window.clearTimeout(pending.timeoutId);
          pending.resolve({ status: "accepted" });
          return;
        }

        if (payload.type === "error") {
          const clientMessageId = payload.client_message_id?.trim();
          if (!clientMessageId) {
            updateSocketState(subdomain, "error");
            authenticatedRefs.current.set(subdomain, false);
            closeManagerSocket(ws, "manager error");
            return;
          }
          const pending = pendingMessagesRef.current.get(clientMessageId);
          if (pending) {
            pendingMessagesRef.current.delete(clientMessageId);
            window.clearTimeout(pending.timeoutId);
            pending.reject(
              new Error(payload.message ?? "Manager request failed."),
            );
            return;
          }
          const folderPending =
            pendingFolderActionsRef.current.get(clientMessageId);
          if (folderPending) {
            pendingFolderActionsRef.current.delete(clientMessageId);
            window.clearTimeout(folderPending.timeoutId);
            folderPending.reject(
              new Error(payload.message ?? "Manager request failed."),
            );
            return;
          }
          const pendingWake =
            pendingWakeRequestsRef.current.get(clientMessageId);
          if (pendingWake) {
            pendingWakeRequestsRef.current.delete(clientMessageId);
            window.clearTimeout(pendingWake.timeoutId);
            pendingWake.reject(
              new Error(payload.message ?? "Manager request failed."),
            );
            return;
          }
          const pendingShutdown =
            pendingShutdownRequestsRef.current.get(clientMessageId);
          if (!pendingShutdown) {
            return;
          }
          pendingShutdownRequestsRef.current.delete(clientMessageId);
          window.clearTimeout(pendingShutdown.timeoutId);
          pendingShutdown.reject(
            new Error(payload.message ?? "Manager request failed."),
          );
        }
      };

      ws.onerror = () => {
        if (cancelled || websocketsRef.current.get(subdomain) !== ws) return;
        updateSocketState(subdomain, "error");
      };

      ws.onclose = () => {
        if (cancelled || websocketsRef.current.get(subdomain) !== ws) return;
        websocketsRef.current.delete(subdomain);
        authenticatedRefs.current.set(subdomain, false);
        markInstanceSessionsOffline(subdomain);
        updateSocketState(subdomain, "offline");
        if (onlineManagerSubdomainsRef.current.includes(subdomain)) {
          scheduleReconnect(subdomain);
        }
      };
    };

    for (const existingSubdomain of Array.from(websocketsRef.current.keys())) {
      if (subdomains.includes(existingSubdomain)) {
        continue;
      }
      const ws = websocketsRef.current.get(existingSubdomain);
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        closeManagerSocket(ws, "manager offline");
      }
      websocketsRef.current.delete(existingSubdomain);
      authenticatedRefs.current.delete(existingSubdomain);
      socketGenerationsRef.current.delete(existingSubdomain);
      markInstanceSessionsOffline(existingSubdomain);
      socketStatesRef.current.delete(existingSubdomain);
      const timer = reconnectTimersRef.current.get(existingSubdomain);
      if (timer != null) {
        window.clearTimeout(timer);
        reconnectTimersRef.current.delete(existingSubdomain);
      }
    }

    for (const sub of subdomains) {
      if (
        !websocketsRef.current.has(sub) &&
        !reconnectTimersRef.current.has(sub)
      ) {
        connect(sub);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [
    markInstanceSessionsOffline,
    onlineManagerSubdomains,
    sendBrowserSessionToManager,
    sendSubscribeInstance,
    updateSocketState,
  ]);

  useEffect(() => {
    const websockets = websocketsRef.current;
    const reconnectTimers = reconnectTimersRef.current;
    return () => {
      for (const ws of websockets.values()) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        closeManagerSocket(ws, "unmount");
      }
      websockets.clear();
      for (const timer of reconnectTimers.values()) {
        window.clearTimeout(timer);
      }
      reconnectTimers.clear();
    };
  }, []);

  const subscribeToInstance = useCallback(
    (instanceId: string, subdomain?: string | null) => {
      const trimmed = instanceId.trim();
      if (!trimmed) return;

      const target = resolveTargetSubdomain(trimmed, subdomain);
      desiredInstanceSubscriptionsRef.current.add(trimmed);
      setInstanceSessions((prev) => {
        const current = prev[trimmed];
        if (current) {
          const nextManagerSubdomain =
            target ?? current.managerSubdomain ?? null;
          if (current.managerSubdomain === nextManagerSubdomain) {
            return prev;
          }
          return {
            ...prev,
            [trimmed]: {
              ...current,
              managerSubdomain: nextManagerSubdomain,
            },
          };
        }
        return {
          ...prev,
          [trimmed]: {
            ...emptyInstanceSessionState(null),
            managerSubdomain: target ?? null,
          },
        };
      });

      if (target && authenticatedRefs.current.get(target)) {
        if (!activeInstanceSubscriptionsRef.current.has(trimmed)) {
          sendSubscribeInstance(trimmed, target);
        }
      }
    },
    [resolveTargetSubdomain, sendSubscribeInstance],
  );

  const unsubscribeFromInstance = useCallback(
    (instanceId: string, subdomain?: string | null) => {
      const trimmed = instanceId.trim();
      if (!trimmed) return;

      desiredInstanceSubscriptionsRef.current.delete(trimmed);
      activeInstanceSubscriptionsRef.current.delete(trimmed);

      const target = resolveTargetSubdomain(trimmed, subdomain);

      if (target) {
        try {
          sendRaw(
            { type: "unsubscribe_instance", instance_id: trimmed },
            target,
          );
        } catch {
          // Ignore
        }
      }
    },
    [resolveTargetSubdomain, sendRaw],
  );

  const sendMessage = useCallback(
    (input: SendMessageInput, subdomain?: string | null) => {
      const text = input.text.trim();
      if (!text) return Promise.reject(new Error("Message cannot be empty."));

      const target = resolveTargetSubdomain(input.instanceId, subdomain);
      if (!target)
        return Promise.reject(
          new Error("No manager available to handle this message."),
        );

      const clientMessageId =
        input.clientMessageId?.trim() ||
        (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

      return new Promise<SendMessageResult>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          const pending = pendingMessagesRef.current.get(clientMessageId);
          if (!pending) return;
          pendingMessagesRef.current.delete(clientMessageId);
          pending.reject(
            new Error("Manager request timed out. Please try again."),
          );
        }, PENDING_MESSAGE_TIMEOUT_MS);

        pendingMessagesRef.current.set(clientMessageId, {
          resolve,
          reject,
          timeoutId,
        });

        try {
          sendRaw(
            {
              type: "send_message",
              client_message_id: clientMessageId,
              instance_id: input.instanceId?.trim() || undefined,
              task_id: input.taskId?.trim() || undefined,
              message_type: input.messageType,
              text,
              reply_to: input.replyTo?.trim() || undefined,
            },
            target,
          );
        } catch (error) {
          const pending = pendingMessagesRef.current.get(clientMessageId);
          if (pending) window.clearTimeout(pending.timeoutId);
          pendingMessagesRef.current.delete(clientMessageId);
          reject(error);
        }
      });
    },
    [resolveTargetSubdomain, sendRaw],
  );

  const refreshManagerFolders = useCallback(
    (subdomain?: string | null) => {
      const target = resolveTargetSubdomain(undefined, subdomain);
      if (!target) {
        return Promise.reject(new Error("No manager is online."));
      }
      const clientMessageId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      return new Promise<void>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          const pending = pendingFolderActionsRef.current.get(clientMessageId);
          if (!pending) {
            return;
          }
          pendingFolderActionsRef.current.delete(clientMessageId);
          pending.reject(
            new Error("Folder update timed out. Please try again."),
          );
        }, PENDING_FOLDER_ACTION_TIMEOUT_MS);
        pendingFolderActionsRef.current.set(clientMessageId, {
          resolve,
          reject,
          timeoutId,
        });
        try {
          sendRaw(
            { type: "refresh_folders", client_message_id: clientMessageId },
            target,
          );
        } catch (error) {
          window.clearTimeout(timeoutId);
          pendingFolderActionsRef.current.delete(clientMessageId);
          reject(error);
        }
      });
    },
    [resolveTargetSubdomain, sendRaw],
  );

  const requestTaskPickup = useCallback(
    async (subdomain?: string | null) => {
      const target = resolveTargetSubdomain(undefined, subdomain);
      if (!target) {
        throw new Error("No manager is online.");
      }
      sendRaw({ type: "pickup_ready_tasks" }, target);
    },
    [resolveTargetSubdomain, sendRaw],
  );

  const wakeInstance = useCallback(
    (input: WakeInstanceInput, subdomain?: string | null) => {
      const instanceId = input.instanceId.trim();
      if (!instanceId)
        return Promise.reject(new Error("Instance id is required."));

      const target = resolveTargetSubdomain(instanceId, subdomain);

      const clientMessageId =
        input.clientMessageId?.trim() ||
        (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

      const payload = {
        type: "wake_instance" as const,
        client_message_id: clientMessageId,
        instance_id: instanceId,
        task_id: input.taskId?.trim() || undefined,
      };

      return new Promise<WakeInstanceResult>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          const pending = pendingWakeRequestsRef.current.get(clientMessageId);
          if (!pending) return;
          pendingWakeRequestsRef.current.delete(clientMessageId);
          pending.reject(
            new Error("Wake request timed out. Please try again."),
          );
        }, PENDING_MESSAGE_TIMEOUT_MS);

        pendingWakeRequestsRef.current.set(clientMessageId, {
          targetSubdomain: target,
          payload,
          sent: false,
          resolve,
          reject,
          timeoutId,
        });

        if (target) {
          try {
            sendRaw(payload, target);
            const pending = pendingWakeRequestsRef.current.get(clientMessageId);
            if (pending) pending.sent = true;
          } catch (error) {
            // Reconnect logic will pick it up if target exists
          }
        } else if (!managerOnline) {
          window.clearTimeout(timeoutId);
          pendingWakeRequestsRef.current.delete(clientMessageId);
          reject(new Error("No manager is online."));
        }
      });
    },
    [managerOnline, resolveTargetSubdomain, sendRaw],
  );

  const shutdownInstance = useCallback(
    (input: ShutdownInstanceInput, subdomain?: string | null) => {
      const instanceId = input.instanceId.trim();
      if (!instanceId) {
        return Promise.reject(new Error("Instance id is required."));
      }

      const target = resolveTargetSubdomain(instanceId, subdomain);
      const clientMessageId =
        input.clientMessageId?.trim() ||
        (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

      const payload = {
        type: "shutdown_instance" as const,
        client_message_id: clientMessageId,
        instance_id: instanceId,
      };

      return new Promise<ShutdownInstanceResult>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          const pending =
            pendingShutdownRequestsRef.current.get(clientMessageId);
          if (!pending) {
            return;
          }
          pendingShutdownRequestsRef.current.delete(clientMessageId);
          pending.reject(
            new Error("Shutdown request timed out. Please try again."),
          );
        }, PENDING_MESSAGE_TIMEOUT_MS);

        pendingShutdownRequestsRef.current.set(clientMessageId, {
          targetSubdomain: target,
          payload,
          sent: false,
          resolve,
          reject,
          timeoutId,
        });

        if (target) {
          try {
            sendRaw(payload, target);
            const pending =
              pendingShutdownRequestsRef.current.get(clientMessageId);
            if (pending) {
              pending.sent = true;
            }
          } catch {
            // Reconnect logic will pick it up if target exists
          }
        } else if (!managerOnline) {
          window.clearTimeout(timeoutId);
          pendingShutdownRequestsRef.current.delete(clientMessageId);
          reject(new Error("No manager is online."));
        }
      });
    },
    [managerOnline, resolveTargetSubdomain, sendRaw],
  );

  const setTerminalSize = useCallback(
    (
      instanceId: string,
      cols: number,
      rows: number,
      subdomain?: string | null,
    ) => {
      const trimmed = instanceId.trim();
      if (!trimmed || cols <= 0 || rows <= 0) return;

      const currentSize = desiredInstanceTerminalSizesRef.current.get(trimmed);
      if (currentSize?.cols === cols && currentSize.rows === rows) return;
      desiredInstanceTerminalSizesRef.current.set(trimmed, { cols, rows });

      const target = resolveTargetSubdomain(trimmed, subdomain);
      if (!desiredInstanceSubscriptionsRef.current.has(trimmed)) return;

      try {
        if (!activeInstanceSubscriptionsRef.current.has(trimmed)) {
          sendSubscribeInstance(trimmed, target);
          return;
        }
        if (target) {
          sendRaw(
            { type: "terminal_resize", instance_id: trimmed, cols, rows },
            target,
          );
        }
      } catch {
        // Ignore
      }
    },
    [resolveTargetSubdomain, sendRaw, sendSubscribeInstance],
  );

  const sendTerminalInput = useCallback(
    (instanceId: string, data: string, subdomain?: string | null) => {
      const trimmed = instanceId.trim();
      if (!trimmed || data.length === 0) return;

      const target = resolveTargetSubdomain(trimmed, subdomain);
      if (!target) return;

      try {
        sendRaw({ type: "terminal_input", instance_id: trimmed, data }, target);
      } catch {
        // Ignore
      }
    },
    [resolveTargetSubdomain, sendRaw],
  );

  const value = useMemo<ManagerConnectionContextValue>(
    () => ({
      managerOnline,
      managerSubdomain,
      activeManagerId,
      activeManagerStatus,
      socketState,
      socketStatesBySubdomain,
      instanceSessions,
      setPreferredManagerSubdomain,
      subscribeToInstance,
      unsubscribeFromInstance,
      sendMessage,
      refreshManagerFolders,
      requestTaskPickup,
      wakeInstance,
      shutdownInstance,
      sendTerminalInput,
      setTerminalSize,
    }),
    [
      activeManagerId,
      activeManagerStatus,
      managerOnline,
      managerSubdomain,
      refreshManagerFolders,
      socketState,
      socketStatesBySubdomain,
      instanceSessions,
      setPreferredManagerSubdomain,
      subscribeToInstance,
      unsubscribeFromInstance,
      sendMessage,
      wakeInstance,
      requestTaskPickup,
      shutdownInstance,
      sendTerminalInput,
      setTerminalSize,
    ],
  );

  return (
    <ManagerConnectionContext.Provider value={value}>
      {children}
    </ManagerConnectionContext.Provider>
  );
}
